import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { decodeTokenPayload, tokenHasPermissions } from '@grc/ui-utility';

// The layout renders once useEnsurePermissionsToken is done (it gives up after 9 s) or the app's
// own cap runs out, and the token it renders with can still lack the permissions: the role can
// land after the hook's last refresh. The menu and the page then get 403s. So once the layout is
// shown, this checks the cached token, and only if it has no permissions fetches a fresh one,
// once, in the background. When that one carries permissions, the returned key changes; the
// layout is mounted under that key, so it mounts again and the menu and the page load with the
// new token. A user who genuinely has no permissions keeps the layout as it is, with its normal
// empty state.
//
// A token this can't read has nothing to check. A failed fetch (say, login_required) also leaves
// the layout as it is; useEnsurePermissionsToken owns the redirect to Auth0.
const usePermissionsTokenRetry = (layoutShown: boolean): number => {
  const { getAccessTokenSilently } = useAuth0();
  const [layoutKey, setLayoutKey] = useState(0);
  // Once per mount; also stops a StrictMode re-run of the effect from fetching twice.
  const retried = useRef(false);

  useEffect(() => {
    if (!layoutShown || retried.current) {
      return;
    }
    retried.current = true;
    getAccessTokenSilently()
      .then(cached => {
        const payload = decodeTokenPayload(cached);
        if (payload === null || tokenHasPermissions(payload)) {
          return undefined;
        }
        return getAccessTokenSilently({ cacheMode: 'off' }).then(fresh => {
          if (tokenHasPermissions(decodeTokenPayload(fresh))) {
            setLayoutKey(key => key + 1);
          }
        });
      })
      .catch(() => undefined);
  }, [layoutShown, getAccessTokenSilently]);

  return layoutKey;
};

export default usePermissionsTokenRetry;
